import React from 'react'
import { Card } from 'primereact/card'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { Dropdown } from 'primereact/dropdown'
import { TabView, TabPanel } from 'primereact/tabview'
import { Tag } from 'primereact/tag'
import { DataView } from 'primereact/dataview'
import { fetchAllZodiacSigns, fetchAllHoroscopePredictions, fetchAllBakthiContent, ZodiacSign, HoroscopePrediction, BakthiContent } from '../api/horoscope'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../store/store'
import { setLanguage } from '../store/slices/languageSlice'
import { toggleMode } from '../store/slices/themeSlice'
import BreadcrumbsNav from '../components/BreadcrumbsNav'
import ClassicAppBar from '../components/ClassicAppBar'

const BakthiPage: React.FC = () => {
    const { t, i18n } = useTranslation()
    const dispatch = useDispatch()
    const themeMode = useSelector((state: RootState) => state.theme?.mode || 'light')
    const [signs, setSigns] = React.useState<ZodiacSign[]>([])
    const [predictions, setPredictions] = React.useState<HoroscopePrediction[]>([])
    const [content, setContent] = React.useState<BakthiContent[]>([])
    const [loading, setLoading] = React.useState(true)
    const [searchQuery, setSearchQuery] = React.useState('')
    const [selectedElement, setSelectedElement] = React.useState<string | null>(null)
    const navigate = useNavigate()

    const handleLanguageChange = (lang: 'en' | 'ta' | 'de') => {
        i18n.changeLanguage(lang)
        dispatch(setLanguage(lang))
    }

    const handleToggleMode = () => {
        dispatch(toggleMode())
    }

    React.useEffect(() => {
        const loadData = async () => {
            setLoading(true)
            try {
                const [signsData, predictionsData, contentData] = await Promise.all([
                    fetchAllZodiacSigns(),
                    fetchAllHoroscopePredictions(),
                    fetchAllBakthiContent()
                ])
                setSigns(signsData)
                setPredictions(predictionsData)
                setContent(contentData)
            } catch (error) {
                console.error('Failed to load bakthi data:', error)
            } finally {
                setLoading(false)
            }
        }
        loadData()
    }, [])

    const filteredSigns = React.useMemo(() => {
        let filtered = signs

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase()
            filtered = filtered.filter(sign =>
                sign.name.toLowerCase().includes(query) ||
                (sign.nameTamil && sign.nameTamil.includes(searchQuery)) ||
                (sign.element && sign.element.toLowerCase().includes(query))
            )
        }

        if (selectedElement) {
            filtered = filtered.filter(sign => sign.element === selectedElement)
        }

        return filtered
    }, [signs, searchQuery, selectedElement])

    const getSignName = (sign: ZodiacSign) => {
        return i18n.language === 'ta' ? sign.nameTamil : i18n.language === 'de' ? sign.nameGerman : sign.name
    }

    const getSignElement = (sign: ZodiacSign) => {
        return i18n.language === 'ta' ? sign.elementTamil : i18n.language === 'de' ? sign.elementGerman : sign.element
    }

    const getSignDescription = (sign: ZodiacSign) => {
        return i18n.language === 'ta' ? sign.descriptionTamil : i18n.language === 'de' ? sign.descriptionGerman : sign.description
    }

    const getContentTitle = (item: BakthiContent) => {
        return i18n.language === 'ta' ? item.titleTamil : i18n.language === 'de' ? item.titleGerman : item.title
    }

    const getContentText = (item: BakthiContent) => {
        return i18n.language === 'ta' ? item.contentTamil : i18n.language === 'de' ? item.contentGerman : item.content
    }

    const getCategoryName = (item: BakthiContent) => {
        return i18n.language === 'ta' ? item.categoryTamil : i18n.language === 'de' ? item.categoryGerman : item.category
    }

    const elements = [
        { label: t('bakthi.elements.all'), value: null },
        { label: t('bakthi.elements.fire'), value: 'Fire' },
        { label: t('bakthi.elements.earth'), value: 'Earth' },
        { label: t('bakthi.elements.air'), value: 'Air' },
        { label: t('bakthi.elements.water'), value: 'Water' }
    ]

    const signTemplate = (sign: ZodiacSign) => {
        return (
            <div className="col-12 sm:col-6 md:col-4 p-3">
                <Card
                    className="h-full border-1 border-gray-300 shadow-2 border-round-xl hover:shadow-4 hover:border-orange-500 transition-all transition-duration-300 text-center"
                >
                    <div className="text-6xl text-orange-500 mb-3">{sign.symbol}</div>
                    <div className="text-2xl font-bold mb-2 font-serif text-900">
                        {getSignName(sign)}
                    </div>
                    <div className="text-gray-500 mb-3 text-sm">{sign.dates}</div>

                    <Tag value={getSignElement(sign)} className="bg-orange-500 text-white mb-3" />

                    <div className="text-gray-600 mb-4 text-sm line-height-3 h-5rem overflow-hidden text-overflow-ellipsis">
                        {getSignDescription(sign)}
                    </div>

                    <div className="flex justify-content-center gap-4 mb-4 text-sm text-gray-700">
                        <div className="flex align-items-center gap-1">
                            <i className="pi pi-star text-orange-500"></i>
                            <span>{t('bakthi.lucky')}: {sign.luckyNumber}</span>
                        </div>
                        <div className="flex align-items-center gap-1">
                            <i className="pi pi-palette text-orange-500"></i>
                            <span>{t('bakthi.color')}: {sign.color}</span>
                        </div>
                    </div>

                    <Button
                        label={t('bakthi.viewHoroscope')}
                        className="w-full p-button-rounded font-bold bg-orange-600 border-orange-600"
                    />
                </Card>
            </div>
        )
    }

    const predictionTemplate = (prediction: HoroscopePrediction) => {
        const sign = signs.find(s => s.signId === prediction.signId)
        if (!sign) return null

        return (
            <div className="col-12 md:col-6 p-3">
                <Card className="h-full border-1 border-gray-300 shadow-2 border-round-xl hover:shadow-4 transition-all transition-duration-300">
                    <div className="flex align-items-center gap-3 mb-3 border-bottom-1 border-gray-200 pb-3">
                        <div className="text-4xl text-orange-500">{sign.symbol}</div>
                        <div>
                            <div className="text-xl font-bold font-serif text-900">{getSignName(sign)}</div>
                            <div className="text-gray-500 text-sm">{prediction.date}</div>
                        </div>
                    </div>

                    <div className="text-gray-700 line-height-3 mb-4">
                        {i18n.language === 'ta' ? prediction.predictionTamil : i18n.language === 'de' ? prediction.predictionGerman : prediction.prediction}
                    </div>

                    <div className="flex gap-2">
                        <Tag value={`${t('bakthi.luck')}: ${prediction.luckyNumber}`} severity="warning" />
                        <Tag value={`${t('bakthi.color')}: ${prediction.luckyColor}`} severity="info" />
                    </div>
                </Card>
            </div>
        )
    }

    const contentTemplate = (item: BakthiContent) => {
        return (
            <div className="col-12 md:col-6 p-3">
                <Card
                    className="h-full border-1 border-gray-300 shadow-2 border-round-xl hover:shadow-4 hover:border-orange-500 transition-all transition-duration-300"
                    header={
                        <img
                            src={item.imageUrl}
                            alt={getContentTitle(item)}
                            className="w-full h-15rem object-cover border-round-top-xl"
                        />
                    }
                >
                    <Tag value={getCategoryName(item)} className="bg-orange-500 text-white mb-2" />
                    <div className="text-xl font-bold mb-2 font-serif text-900">{getContentTitle(item)}</div>
                    <div className="text-gray-600 line-height-3 mb-3 text-sm h-4rem overflow-hidden text-overflow-ellipsis">
                        {getContentText(item)}
                    </div>
                    <Button
                        label={t('bakthi.readArticle')}
                        className="p-button-text pl-0 text-orange-600 hover:bg-orange-50"
                        icon="pi pi-arrow-right"
                        iconPos="right"
                    />
                </Card>
            </div>
        )
    }

    return (
        <div className="min-h-screen flex flex-column bg-gray-50">
            <Helmet>
                <title>Bakthi · YarlHub</title>
                <meta name="description" content="Tamil horoscope, astrology and spiritual content" />
                <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : undefined} />
            </Helmet>

            <ClassicAppBar
                mode={themeMode}
                onToggleMode={handleToggleMode}
                onChangeLang={handleLanguageChange}
            />

            <div className="container mx-auto p-4 lg:p-6 max-w-7xl">
                {/* Header Section */}
                <div className="mb-5">
                    <Button
                        label={t('back') || 'Back'}
                        icon="pi pi-arrow-left"
                        className="p-button-text text-orange-600 mb-2 pl-0 hover:bg-orange-50"
                        onClick={() => navigate('/')}
                    />

                    <BreadcrumbsNav items={[{ label: 'Bakthi' }]} />

                    <div className="flex align-items-center gap-3 mt-3 mb-4">
                        <i className="pi pi-compass text-4xl text-orange-600"></i>
                        <h1 className="text-4xl md:text-5xl font-bold font-serif text-900 m-0 bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-orange-600" style={{ backgroundClip: 'text', WebkitBackgroundClip: 'text', backgroundImage: 'linear-gradient(45deg, #1a252f 30%, #d35400 90%)', color: 'transparent' }}>
                            {t('bakthi.title')}
                        </h1>
                    </div>
                </div>

                <TabView className="mb-5">
                    <TabPanel header={t('bakthi.tabs.zodiac')} leftIcon="pi pi-star">
                        {loading ? (
                            <div className="flex justify-content-center p-8">
                                <i className="pi pi-spin pi-spinner text-orange-500 text-4xl"></i>
                            </div>
                        ) : (
                            <>
                                {/* Filters */}
                                <div className="grid mb-5">
                                    <div className="col-12 md:col-6">
                                        <span className="p-input-icon-left w-full">
                                            <i className="pi pi-search text-orange-500" />
                                            <InputText
                                                placeholder={t('bakthi.searchPlaceholder')}
                                                value={searchQuery}
                                                onChange={(e) => { setSearchQuery(e.target.value) }}
                                                className="w-full border-round-xl"
                                            />
                                        </span>
                                    </div>
                                    <div className="col-12 md:col-3">
                                        <Dropdown
                                            value={selectedElement}
                                            options={elements}
                                            onChange={(e) => { setSelectedElement(e.value) }}
                                            placeholder={t('bakthi.elementPlaceholder')}
                                            className="w-full border-round-xl"
                                            showClear
                                        />
                                    </div>
                                    <div className="col-12 md:col-3">
                                        <Button
                                            label={t('bakthi.clear')}
                                            icon="pi pi-filter-slash"
                                            className="p-button-outlined border-orange-500 text-orange-600 w-full"
                                            onClick={() => {
                                                setSearchQuery('')
                                                setSelectedElement(null)
                                            }}
                                        />
                                    </div>
                                </div>

                                <DataView
                                    value={filteredSigns}
                                    itemTemplate={signTemplate}
                                    paginator
                                    rows={12}
                                    className="w-full"
                                    layout="grid"
                                    emptyMessage={t('bakthi.noSigns')}
                                    paginatorClassName="border-none bg-transparent mt-4"
                                />
                            </>
                        )}
                    </TabPanel>

                    <TabPanel header={t('bakthi.tabs.horoscope')} leftIcon="pi pi-moon">
                        {loading ? (
                            <div className="flex justify-content-center p-8">
                                <i className="pi pi-spin pi-spinner text-orange-500 text-4xl"></i>
                            </div>
                        ) : (
                            <DataView
                                value={predictions}
                                itemTemplate={predictionTemplate}
                                paginator
                                rows={6}
                                className="w-full"
                                layout="grid"
                                emptyMessage={t('bakthi.noPredictions')}
                                paginatorClassName="border-none bg-transparent mt-4"
                            />
                        )}
                    </TabPanel>

                    <TabPanel header={t('bakthi.tabs.spiritual')} leftIcon="pi pi-book">
                        {loading ? (
                            <div className="flex justify-content-center p-8">
                                <i className="pi pi-spin pi-spinner text-orange-500 text-4xl"></i>
                            </div>
                        ) : (
                            <DataView
                                value={content}
                                itemTemplate={contentTemplate}
                                paginator
                                rows={6}
                                className="w-full"
                                layout="grid"
                                emptyMessage={t('bakthi.noContent')}
                                paginatorClassName="border-none bg-transparent mt-4"
                            />
                        )}
                    </TabPanel>
                </TabView>

            </div>
        </div>
    )
}

export default BakthiPage
